// FILE: src/Components/Dashboard/SignalsFeed.js (FULLY UPDATED)

import React, { useEffect, useState, useCallback } from 'react';

import axiosServices from '../../services/axios';
import moment from 'moment';
import DOMPurify from 'dompurify';

// --- Skeleton Component for a superior loading experience ---
const BlogCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="aspect-square bg-gray-200 animate-pulse"></div>
        <div className="p-5 space-y-4">
            <div className="h-4 w-1/3 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="space-y-2">
                <div className="h-6 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="h-5 w-1/5 bg-gray-200 rounded animate-pulse"></div>
            </div>
        </div>
    </div>
);

// --- Polished Blog Card Component ---
const BlogCard = ({ post }) => {
    const cleanTitle = DOMPurify.sanitize(post.title);
    const cleanExcerpt = DOMPurify.sanitize(post.excerpt);

    return (
        <a 
            href={post.link} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="group flex flex-col bg-white rounded-xl shadow-md border border-gray-200 hover:border-brand-primary hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden"
        >
            {post.imageUrl && (
                <div className="aspect-square overflow-hidden">
                    <img 
                        src={post.imageUrl} 
                        alt={post.title} 
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                    />
                </div>
            )}
            <div className="p-5 flex flex-col flex-grow">
                <p className="text-xs font-semibold text-indigo-700 bg-indigo-100 uppercase rounded-full self-start px-3 py-1 mb-3 flex items-center gap-2">
                    <i className="fa-solid fa-lightbulb"></i>
                    <span>Marketing Tip</span>
                </p>
                <h4 
                    className="font-bold text-lg text-gray-900 leading-tight mb-2 flex-grow transition-colors group-hover:text-brand-primary" 
                    dangerouslySetInnerHTML={{ __html: cleanTitle }} 
                />
                <p 
                    className="text-sm text-gray-600 line-clamp-3" 
                    dangerouslySetInnerHTML={{ __html: cleanExcerpt }} 
                />
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                    <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                        <img src="https://i.pravatar.cc/24?u=oolook" alt="Author" className="w-6 h-6 rounded-full" />
                        <span>Oolook Team · {moment(post.date).fromNow()}</span>
                    </div>
                    <span className="text-sm font-semibold text-brand-primary">
                        Read More <i className="fa-solid fa-arrow-right ml-1 transition-transform group-hover:translate-x-1.5"></i>
                    </span>
                </div>
            </div>
        </a>
    );
};

// --- Main SignalsFeed Component ---
export default function SignalsFeed() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBlogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Simulate a slightly longer loading time to showcase the skeleton loader
            await new Promise(resolve => setTimeout(resolve, 500)); 
            const response = await axiosServices.get('/common/blogs');
            setPosts(response.data);
        } catch (err) {
            setError("Could not load Signals feed. Please try again later.");
            console.error("Error fetching blog posts:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    const renderContent = () => {
        if (loading) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Array(3).fill(0).map((_, index) => <BlogCardSkeleton key={index} />)}
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex flex-col items-center justify-center h-64 text-center p-4 bg-red-50 rounded-lg border border-red-200">
                    <i className="fa-solid fa-circle-exclamation text-4xl text-red-400 mb-4"></i>
                    <p className="font-semibold text-red-700">{error}</p>
                </div>
            );
        }

        if (posts.length > 0) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map(post => (
                        <BlogCard key={post.id} post={post} />
                    ))}
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center h-64 text-center p-4 bg-gray-50 rounded-lg border-2 border-dashed">
                <i className="fa-solid fa-satellite-dish text-4xl text-gray-300 mb-4"></i>
                <p className="font-semibold text-gray-700">No signals to display right now.</p>
                <p className="text-sm text-gray-500 mt-1">Check back later for the latest marketing tips and product updates!</p>
            </div>
        );
    };

    return (
        <div className="bg-gradient-to-b from-white to-gray-50 p-6 rounded-xl shadow-lg border animate-fade-in">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100">
                            <i className="fa-solid fa-lightbulb text-yellow-500"></i>
                        </span>
                        Signals
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Your daily dose of social media wisdom and product updates.</p>
                </div>
                <a href="https://oolook.in/blog" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-brand-primary hover:underline">
                    View All <i className="fa-solid fa-arrow-right ml-1"></i>
                </a>
            </div>
            {renderContent()}
        </div>
    );
}